// =============================================================================
// FILE: packages/renderer/src/hooks/useWorkspacePrefs.ts
// PURPOSE: Per-USER persisted workspace preferences.
//   Storage key is scoped to the logged-in user: bim.prefs.v2.{userEmail}
//   so every user gets their own settings, wallpaper, effects, and sport/team.
//   Backward compatible — migrates the old shared key on first user login.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'default' | 'neon-cyberpunk';
export type UITextMode = 'normal' | 'uppercase';
export type WorkspaceLayout = 'comfortable' | 'compact' | 'minimal';
export type WorkspaceShell = 'professional' | 'mission-control';
export type BackgroundTheme =
  | 'default-dark'
  | 'arctic-slate'
  | 'warm-graphite'
  | 'light-gray'
  | 'solid-black'
  | 'gradient-blue'
  | 'custom'
  | 'wallpaper';

export interface WorkspacePrefs {
  openLastProject: boolean;
  lastProjectId:   string | null;
  theme:           ThemeMode;
  debugIpc:        boolean;
  uiTextMode:      UITextMode;
  minimalMode:     boolean;
  backgroundTheme: BackgroundTheme;
  customBackgroundColor: string;
  alwaysOnTop:     boolean;
  neonMode:        boolean;
  waveMode:        boolean;
  focusMode:       boolean;
  showInviteButtons: boolean;
  showMaps:          boolean;
  showPricing:       boolean;
  workspaceLayout:   WorkspaceLayout;
  workspaceShell:    WorkspaceShell;

  // ── Effects & Animation ─────────────────────────────────────────────────
  /** Master intensity scale for all visual effects (0–1). */
  effectsIntensity:        number;   // default 0.8
  /** WarpDrive canvas opacity (0–1). User-controlled transparency. */
  warpOpacity:             number;   // default 0.85
  /** Auto-fire warp on login, AI thinking, generation events. */
  warpAutoTrigger:         boolean;  // default true
  /** Background animation canvas enabled at all. */
  backgroundEffectsEnabled: boolean; // default true

  // ── Wallpaper ────────────────────────────────────────────────────────────
  /** URL of the active wallpaper image. Empty = no wallpaper. */
  wallpaperUrl:     string;          // default ''
  /** Wallpaper layer opacity (0.05–0.55). */
  wallpaperOpacity: number;          // default 0.18

  // ── Sport & Team Identity ────────────────────────────────────────────────
  sport: string;                     // e.g. 'Hockey', 'Basketball'
  team:  string;                     // e.g. 'Toronto Maple Leafs'
}

// ── Storage keys ─────────────────────────────────────────────────────────────
const LEGACY_KEY     = 'bim.workspace.prefs.v1';
const LEGACY_DEBUG_KEY = 'bim.debug';
const USER_KEY_PREFIX  = 'bim.prefs.v2.';

// Module-level active key — switches on initUserPrefs(email)
let ACTIVE_KEY = LEGACY_KEY;

const DEFAULTS: WorkspacePrefs = {
  openLastProject: true,
  lastProjectId:   null,
  theme:           'default',
  debugIpc:        false,
  uiTextMode:      'normal',
  minimalMode:     false,
  backgroundTheme: 'arctic-slate',
  customBackgroundColor: '#1b2230',
  alwaysOnTop:     false,
  neonMode:        false,
  waveMode:        true,
  focusMode:       false,
  showInviteButtons: true,
  showMaps:          true,
  showPricing:       true,
  workspaceLayout:   'comfortable',
  workspaceShell:     'professional',
  // effects
  effectsIntensity:         0.8,
  warpOpacity:              0.85,
  warpAutoTrigger:          true,
  backgroundEffectsEnabled: false, // default OFF — user must opt in
  // wallpaper
  wallpaperUrl:     '',
  wallpaperOpacity: 0.18,
  // sport & team
  sport: '',
  team:  '',
};

const BG_THEMES: ReadonlySet<BackgroundTheme> = new Set([
  'default-dark', 'arctic-slate', 'warm-graphite',
  'light-gray', 'solid-black', 'gradient-blue', 'custom', 'wallpaper',
]);

function isHex(v: unknown): v is string {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}
function clamp01(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? fallback : Math.max(0, Math.min(1, n));
}

function readPrefs(): WorkspacePrefs {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return { ...DEFAULTS };
    const p = JSON.parse(raw) as Partial<WorkspacePrefs>;
    return {
      ...DEFAULTS, ...p,
      theme:         p.theme === 'neon-cyberpunk' ? 'neon-cyberpunk' : 'default',
      uiTextMode:    p.uiTextMode === 'uppercase' ? 'uppercase' : 'normal',
      openLastProject: !!p.openLastProject,
      debugIpc:        !!p.debugIpc,
      lastProjectId:   typeof p.lastProjectId === 'string' ? p.lastProjectId : null,
      minimalMode:     !!p.minimalMode,
      backgroundTheme: p.backgroundTheme && BG_THEMES.has(p.backgroundTheme)
        ? p.backgroundTheme : 'arctic-slate',
      customBackgroundColor: isHex(p.customBackgroundColor) ? p.customBackgroundColor : '#1b2230',
      alwaysOnTop: !!p.alwaysOnTop,
      neonMode:    !!p.neonMode,
      waveMode:    p.waveMode === undefined ? true : !!p.waveMode,
      focusMode:   !!p.focusMode,
      showInviteButtons: p.showInviteButtons  === undefined ? true : !!p.showInviteButtons,
      showMaps:          p.showMaps           === undefined ? true : !!p.showMaps,
      showPricing:       p.showPricing        === undefined ? true : !!p.showPricing,
      workspaceLayout: (['comfortable', 'compact', 'minimal'] as WorkspaceLayout[]).includes(p.workspaceLayout as WorkspaceLayout)
        ? (p.workspaceLayout as WorkspaceLayout) : 'comfortable',
      workspaceShell: (['professional', 'mission-control'] as WorkspaceShell[]).includes(p.workspaceShell as WorkspaceShell)
        ? (p.workspaceShell as WorkspaceShell) : 'professional',
      // effects
      effectsIntensity:         clamp01(p.effectsIntensity, 0.8),
      warpOpacity:              clamp01(p.warpOpacity, 0.85),
      warpAutoTrigger:          p.warpAutoTrigger          === undefined ? true : !!p.warpAutoTrigger,
      backgroundEffectsEnabled: p.backgroundEffectsEnabled === undefined ? false : !!p.backgroundEffectsEnabled,
      // wallpaper
      wallpaperUrl:     typeof p.wallpaperUrl === 'string' ? p.wallpaperUrl : '',
      wallpaperOpacity: clamp01(p.wallpaperOpacity !== undefined ? p.wallpaperOpacity / (p.wallpaperOpacity > 1 ? 1 : 1) : 0.18, 0.18),
      // sport & team
      sport: typeof p.sport === 'string' ? p.sport : '',
      team:  typeof p.team  === 'string' ? p.team  : '',
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function writePrefs(prefs: WorkspacePrefs): void {
  try {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(prefs));
    if (prefs.debugIpc) localStorage.setItem(LEGACY_DEBUG_KEY, '1');
    else                localStorage.removeItem(LEGACY_DEBUG_KEY);
  } catch { /* quota exceeded — silent */ }
}

// Module-level listeners
type Listener = (prefs: WorkspacePrefs) => void;
const listeners = new Set<Listener>();
function emit(prefs: WorkspacePrefs): void {
  for (const l of listeners) { try { l(prefs); } catch { /* never crash */ } }
}

// ── Per-User Init ─────────────────────────────────────────────────────────────
/**
 * Called on login with the user's email (stable identity).
 * Switches the active storage key to the user-scoped slot, migrating
 * from the shared legacy key if the user slot is empty.
 */
export function initUserPrefs(userEmail: string): void {
  const safe = userEmail.toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
  const userKey = `${USER_KEY_PREFIX}${safe}`;

  if (!localStorage.getItem(userKey)) {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) localStorage.setItem(userKey, legacy);
  }

  ACTIVE_KEY = userKey;
  emit(readPrefs());
}

/** Called on logout — reverts to the anonymous shared key. */
export function resetUserPrefsScope(): void {
  ACTIVE_KEY = LEGACY_KEY;
  emit(readPrefs());
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useWorkspacePrefs(): {
  prefs: WorkspacePrefs;
  setPref<K extends keyof WorkspacePrefs>(key: K, value: WorkspacePrefs[K]): void;
  reset(): void;
} {
  const [prefs, setPrefs] = useState<WorkspacePrefs>(() => readPrefs());

  useEffect(() => {
    const listener: Listener = (next) => setPrefs(next);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const setPref = useCallback(<K extends keyof WorkspacePrefs>(key: K, value: WorkspacePrefs[K]) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      writePrefs(next);
      emit(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    writePrefs({ ...DEFAULTS });
    emit({ ...DEFAULTS });
    setPrefs({ ...DEFAULTS });
  }, []);

  return { prefs, setPref, reset };
}

export function readWorkspacePrefsSync(): WorkspacePrefs { return readPrefs(); }
